import { css } from "styled-system/css";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LoadingFrame } from "@/components/ui/loading-frame";

type OrgJson = {
  id: string;
  name: string;
  slug: string;
  professionals: {
    id: string;
    name: string;
    slug: string;
    category: string | null;
    avatarUrl: string | null;
    primaryColor: string;
    timezone: string;
    services: { id: string; name: string; duration: number; price: number | null; requiresDeposit: boolean; depositAmount: number | null }[];
  }[];
};

export function OrgBookingPage() {
  const { slug } = useParams<{ slug: string }>();
  const [org, setOrg] = useState<OrgJson | null | "loading">("loading");
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    void fetch(`/api/public/organization/${encodeURIComponent(slug)}`)
      .then((r) => {
        if (r.status === 404) setOrg(null);
        return r.json();
      })
      .then((j) => {
        if (cancelled) return;
        if (j?.id) setOrg(j as OrgJson);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (org === "loading") {
    return (
      <div className={css({ minH: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: "6", bg: "bg.muted" })}>
        <div className={css({ w: "100%", maxW: "720px" })}>
          <LoadingFrame variant="light" minHeight={360} />
        </div>
      </div>
    );
  }

  if (!org || !slug) {
    return (
      <div className={css({ minH: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: "6", bg: "bg.muted" })}>
        <p className={css({ color: "#64748b" })}>Organización no encontrada.</p>
      </div>
    );
  }

  return (
    <div className={css({ minH: "100vh", bg: "bg.muted" })}>
      <header
        className={css({
          borderBottomWidth: "1px",
          borderColor: "border.DEFAULT",
          py: "6",
          px: "4",
          bg: "bg.card",
        })}
      >
        <div
          className={css({
            maxW: "720px",
            mx: "auto",
            display: "flex",
            alignItems: "center",
            gap: "3",
          })}
        >
          <div className={css({ w: "10", h: "10", rounded: "full", bg: "bg.muted" })} />
          <div>
            <h1 className={css({ fontSize: "xl", fontWeight: "bold", color: "fg.DEFAULT" })}>{org.name}</h1>
            <p className={css({ fontSize: "sm", color: "fg.muted" })}>Elegí un profesional para reservar</p>
          </div>
        </div>
      </header>

      <main className={css({ maxW: "720px", mx: "auto", p: "4", pb: "16" })}>
        <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
          {org.professionals.map((p) => (
            <Card key={p.id} className={css({ p: "5" })}>
              <div className={css({ display: "flex", gap: "4", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" })}>
                <div>
                  <p className={css({ fontSize: "sm", color: "#64748b" })}>{p.category ?? "Profesional"}</p>
                  <p className={css({ fontSize: "lg", fontWeight: "semibold", color: "#0f172a" })}>{p.name}</p>
                  <p className={css({ fontSize: "xs", color: "#64748b", mt: "1" })}>Zona horaria: {p.timezone}</p>
                </div>
                <div>
                  <Button
                    variant="primary"
                    onClick={() => {
                      window.location.href = `/book/${p.slug}`;
                    }}
                  >
                    Reservar con {p.name}
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}

